import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Loader2, X, CheckCircle2, XCircle, PhoneMissed, Copy, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { DailyVideoCall } from './DailyVideoCall';

interface UserWithStatus {
  id: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  avatar_url?: string;
  status: 'ringing' | 'rejected' | 'accepted';
  rejectedAt?: string;
}

interface DailyRingProgressModalProps {
  ringRequestId: string;
  initialLevel: number;
  initialTimeout: number;
  initialUsers: any[];
  onClose: () => void;
}

export function DailyRingProgressModal({
  ringRequestId,
  initialLevel,
  initialTimeout,
  initialUsers,
  onClose,
}: DailyRingProgressModalProps) {
  const { t } = useTranslation();

  const levelNameKeys = ['firstResponders', 'management', 'finalEscalation'];
  const getLevelName = (level: number) => {
    const key = levelNameKeys[level - 1];
    return key ? t(`videoRing.levelNames.${key}`) : `Level ${level}`;
  };

  const [currentLevel, setCurrentLevel] = useState(initialLevel);
  const [levelName, setLevelName] = useState(getLevelName(initialLevel));
  const [users, setUsers] = useState<UserWithStatus[]>(
    initialUsers.map(u => ({ ...u, status: 'ringing' as const }))
  );
  const [previousLevelUsers, setPreviousLevelUsers] = useState<UserWithStatus[]>([]);
  const [timeRemaining, setTimeRemaining] = useState(initialTimeout);
  const [status, setStatus] = useState<'ringing' | 'accepted' | 'timeout' | 'cancelled'>('ringing');
  const [acceptedBy, setAcceptedBy] = useState<{ name: string; initials: string } | null>(null);

  // Daily-specific state
  const [dailyRoomUrl, setDailyRoomUrl] = useState<string | null>(null);
  const [dailyToken, setDailyToken] = useState<string | null>(null);
  const [clientJoinUrl, setClientJoinUrl] = useState<string | null>(null);

  const isEscalatingRef = useRef(false);

  // Subscribe to ring request changes
  useEffect(() => {
    const channel = supabase
      .channel(`daily-ring-progress-${ringRequestId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'video_call_ring_requests' as any,
          filter: `id=eq.${ringRequestId}`,
        },
        async (payload: any) => {
          const ringRequest = payload.new;
          const metadata = ringRequest.metadata || {};

          // Update rejections
          const rejections = metadata.rejections || [];
          setUsers(prev => prev.map(user => {
            const rejection = rejections.find((r: any) => r.user_id === user.id);
            return rejection ? { ...user, status: 'rejected' as const, rejectedAt: rejection.rejected_at } : user;
          }));
          setPreviousLevelUsers(prev => prev.map(user => {
            const rejection = rejections.find((r: any) => r.user_id === user.id);
            return rejection ? { ...user, status: 'rejected' as const, rejectedAt: rejection.rejected_at } : user;
          }));

          if (ringRequest.status === 'accepted') {
            setStatus('accepted');
            setUsers(prev => prev.map(user =>
              user.id === ringRequest.accepted_by
                ? { ...user, status: 'accepted' as const }
                : user
            ));

            if (ringRequest.accepted_by) {
              const { data: consultant } = await supabase
                .from('app_users')
                .select('first_name, last_name, display_name')
                .eq('id', ringRequest.accepted_by)
                .single();

              if (consultant) {
                const name = consultant.display_name || `${consultant.first_name} ${consultant.last_name}`;
                const initials = name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
                setAcceptedBy({ name, initials });
                toast.success(`${name} aceitou a videochamada!`);
              }
            }

            // Set Daily room URL and requester token so SDR joins the call
            if (ringRequest.meeting_url) {
              setDailyRoomUrl(ringRequest.meeting_url);
            }
            const meta = ringRequest.metadata || {};
            if (meta.requester_token) {
              setDailyToken(meta.requester_token);
            }
            if (meta.client_join_url) {
              setClientJoinUrl(meta.client_join_url);
            }
          } else if (ringRequest.status === 'timeout') {
            setStatus('timeout');
            toast.error(t('videoRing.toasts.noConsultantsAvailable'));
            setTimeout(() => onClose(), 3000);
          } else if (ringRequest.status === 'cancelled') {
            setStatus('cancelled');
            onClose();
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [ringRequestId, onClose, t]);

  // Timer countdown
  useEffect(() => {
    if (status !== 'ringing') return;
    const interval = setInterval(() => {
      setTimeRemaining(prev => prev <= 1 ? 0 : prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [status, currentLevel]);

  useEffect(() => { isEscalatingRef.current = false; }, [currentLevel]);

  useEffect(() => {
    if (timeRemaining === 0 && status === 'ringing' && !isEscalatingRef.current) {
      isEscalatingRef.current = true;
      handleEscalate();
    }
  }, [timeRemaining, status]);

  const handleEscalate = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('escalate-video-ring', {
        body: { ring_request_id: ringRequestId },
      });
      if (error) throw error;

      if (data.status === 'timeout') {
        setStatus('timeout');
        setTimeout(() => onClose(), 3000);
        return;
      }

      setPreviousLevelUsers(prev => [
        ...prev,
        ...users.map(u => u.status === 'ringing' ? { ...u, status: 'rejected' as const } : u)
      ]);
      setCurrentLevel(data.current_level);
      setTimeRemaining(data.timeout_seconds);
      setUsers(data.available_users.map((u: any) => ({ ...u, status: 'ringing' as const })));
      setLevelName(getLevelName(data.current_level));
    } catch (error) {
      console.error('Escalation failed:', error);
      toast.error(t('videoRing.toasts.failedToEscalate'));
    }
  };

  const handleCancel = async () => {
    try {
      const { data: ringRequestData } = await supabase
        .from('video_call_ring_requests' as any)
        .select('call_id')
        .eq('id', ringRequestId)
        .single();

      const callIdToUpdate = (ringRequestData as any)?.call_id;

      await supabase
        .from('video_call_ring_requests' as any)
        .update({ status: 'cancelled', ended_at: new Date().toISOString() })
        .eq('id', ringRequestId);

      if (callIdToUpdate) {
        await supabase
          .from('calls')
          .update({ status: 'cancelled', ended_at: new Date().toISOString() })
          .eq('id', callIdToUpdate);
      }
      onClose();
    } catch (error) {
      console.error('Failed to cancel:', error);
    }
  };

  const copyClientLink = () => {
    if (clientJoinUrl) {
      navigator.clipboard.writeText(clientJoinUrl);
      toast.success('Link copiado! Envie ao cliente via WhatsApp.');
    }
  };

  const progressPercent = ((initialTimeout - timeRemaining) / initialTimeout) * 100;

  const getUserName = (user: UserWithStatus) =>
    user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'User';

  const getUserInitials = (user: UserWithStatus) =>
    getUserName(user).split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

  const renderUserStatus = (user: UserWithStatus) => {
    switch (user.status) {
      case 'accepted':
        return (
          <Badge variant="default" className="gap-1 bg-green-100 text-green-700 border-0">
            <CheckCircle2 className="h-3 w-3" />
            Atendeu
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="gap-1 border-orange-300 text-orange-600">
            <XCircle className="h-3 w-3" />
            Recusou
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t('videoRing.ringing')}
          </Badge>
        );
    }
  };

  // When accepted, fetch the client join URL from meeting data
  useEffect(() => {
    if (status !== 'accepted' || !ringRequestId) return;

    const fetchMeetingDetails = async () => {
      // Get the ring request to find call_id, then find meeting
      const { data: ringReq } = await supabase
        .from('video_call_ring_requests' as any)
        .select('call_id, meeting_url')
        .eq('id', ringRequestId)
        .single();

      if (!ringReq) return;

      const rr = ringReq as any;
      if (rr.meeting_url) setDailyRoomUrl(rr.meeting_url);

      // Find meeting by deal/contact from the call
      const { data: callData } = await supabase
        .from('calls')
        .select('deal_id, contact_id')
        .eq('id', rr.call_id)
        .single();

      if (!callData) return;

      // Find the latest meeting for this deal
      const { data: meeting } = await supabase
        .from('meetings' as any)
        .select('id')
        .eq('deal_id', callData.deal_id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (meeting) {
        setClientJoinUrl(`${window.location.origin}/meet/${(meeting as any).id}`);
      }
    };

    fetchMeetingDetails();
  }, [status, ringRequestId]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className={status === 'accepted' ? 'max-w-[95vw] w-full h-[90vh] p-0 gap-0 flex flex-col' : 'max-w-md'}>
        <DialogTitle className={status === 'accepted' ? 'px-4 py-3 border-b text-base' : 'text-center'}>
          {status === 'ringing' && t('videoRing.callingConsultants')}
          {status === 'accepted' && 'Videochamada Daily'}
          {status === 'timeout' && t('videoRing.noConsultantsAvailable')}
          {status === 'cancelled' && t('videoRing.callCancelled')}
        </DialogTitle>

        {status === 'ringing' && (
          <div className="space-y-6 py-4 px-4">
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-2">
                <Badge variant="outline">Nível {currentLevel}</Badge>
                <h3 className="text-lg font-semibold">{levelName}</h3>
              </div>
              <Progress value={progressPercent} className="h-2" />
              <p className="text-sm text-muted-foreground">
                {t('videoRing.callingCount', { count: users.filter(u => u.status === 'ringing').length })}
              </p>
              <div className="text-3xl font-bold text-primary tabular-nums">
                {timeRemaining}s
              </div>
            </div>

            {previousLevelUsers.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Níveis Anteriores:</p>
                <div className="space-y-1 opacity-60">
                  {previousLevelUsers.map((user) => (
                    <div key={user.id} className="flex items-center gap-2 p-1.5 rounded">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">{getUserInitials(user)}</AvatarFallback>
                      </Avatar>
                      <span className="flex-1 text-sm">{getUserName(user)}</span>
                      <Badge variant="outline" className="gap-1 text-xs border-orange-300 text-orange-600">
                        <PhoneMissed className="h-3 w-3" />
                        {user.status === 'rejected' ? 'Recusou' : 'Missed'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ScrollArea className="h-48 border rounded-lg p-2">
              <div className="space-y-2">
                {users.map((user) => (
                  <div key={user.id} className="flex items-center gap-3 p-2 rounded hover:bg-muted/50">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">{getUserInitials(user)}</AvatarFallback>
                    </Avatar>
                    <span className="flex-1 text-sm">{getUserName(user)}</span>
                    {renderUserStatus(user)}
                  </div>
                ))}
              </div>
            </ScrollArea>

            <Button onClick={handleCancel} variant="outline" className="w-full">
              <X className="mr-2 h-4 w-4" />
              {t('videoRing.cancelCall')}
            </Button>
          </div>
        )}

        {status === 'accepted' && (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Header with accepted info + copy link */}
            <div className="px-4 py-2 border-b bg-green-50 dark:bg-green-950 flex items-center gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
              {acceptedBy && (
                <span className="text-sm font-medium">{acceptedBy.name} aceitou</span>
              )}
              <div className="ml-auto flex items-center gap-2">
                {clientJoinUrl && (
                  <Button variant="outline" size="sm" onClick={copyClientLink} className="gap-1.5">
                    <Copy className="w-3.5 h-3.5" />
                    Copiar link do cliente
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={onClose}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Daily video call embedded for SDR */}
            <div className="flex-1 min-h-0">
              {dailyRoomUrl && dailyToken ? (
                <DailyVideoCall
                  roomUrl={dailyRoomUrl}
                  token={dailyToken}
                  onMeetingEnded={onClose}
                />
              ) : (
                <div className="flex items-center justify-center h-full p-8">
                  <div className="text-center space-y-4 max-w-md">
                    <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
                    <p className="text-sm text-muted-foreground">Conectando à sala de vídeo...</p>
                    {clientJoinUrl && (
                      <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                        <p className="text-sm text-muted-foreground">Link do cliente para compartilhar:</p>
                        <div className="flex items-center gap-2">
                          <ExternalLink className="w-3 h-3 flex-shrink-0 text-muted-foreground" />
                          <span className="text-xs truncate">{clientJoinUrl}</span>
                        </div>
                        <Button onClick={copyClientLink} size="sm" className="w-full gap-1.5">
                          <Copy className="w-3.5 h-3.5" />
                          Copiar link
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {status === 'timeout' && (
          <div className="text-center space-y-4 py-4 px-4">
            <XCircle className="h-16 w-16 text-destructive mx-auto" />
            <p className="text-muted-foreground">{t('videoRing.noConsultantsMessage')}</p>
            <Button onClick={onClose} className="w-full">{t('common.close')}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
